import React, { FunctionComponent, useEffect, useState } from "react";
import {
  CampaignOfferType,
  SingleProductOffer,
  CampaignDiscountType,
  CampaignTargetUserType,
  CampaignProductSelectionType
} from "../../models/Campaign";
import { useDispatch, useSelector } from "react-redux";
import { StoreInventoryItem } from "../../models/StoreInventory";
import { thunkFetchAllStoreInventoryItems } from "../../store/stores/actions";
import MaterialTable from "@material-table/core";
import {
  makeStyles,
  Theme,
  createStyles,
  TextField,
  Typography,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Dialog,
  DialogContent,
  IconButton,
  Button,
  useTheme,
  useMediaQuery
} from "@material-ui/core";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import { AppState } from "../../store";
import Modal from "../../components/dialogs/modal";
import { AllInventoryItemsState } from "../../store/stores/types";
import { Alert, AlertTitle } from "@material-ui/lab";
import { Currency } from "../../components/misc/Currency";
import { ProductState } from "../../store/products/types";
import { Product } from "../../models/Product";
import { CampaignsState } from "../../store/campaigns/types";
import { useI18n } from "../../hooks/useI18n";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    closeButton: {
      position: "absolute",
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500]
    }
  })
);

interface Props {
  storeIds: string[];
  campaignTargetUserType: CampaignTargetUserType;
  campaignDiscountType: CampaignDiscountType;
  minimumPurchaseQuantity?: number;
  minimumPurchaseAmount?: number;
  excludedProductIds?: string[];

  onSave: (campaignProduct: SingleProductOffer) => void;
}

export const CampaignPriceSelector: FunctionComponent<Props> = ({
  storeIds,
  campaignTargetUserType,
  minimumPurchaseQuantity,
  minimumPurchaseAmount,
  excludedProductIds,
  campaignDiscountType,
  onSave
}) => {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useI18n();

  const classes = useStyles();
  const productState = useSelector<AppState, ProductState>(
    s => s.storeClusterProducts.productState
  );

  const { products, isLoading } = productState;

  const [dialogOpenState, setDialogOpenState] = useState<boolean>(false);
  const [offer, setOffer] = useState<SingleProductOffer>({
    product: { id: "", name: "" }
  });

  const [purchaseRequirement, setPurchaseRequirement] = useState<string>(() => {
    if (minimumPurchaseQuantity !== undefined) {
      return "NumberOfItems";
    }
    if (minimumPurchaseAmount !== undefined) {
      return "ShoppingCartAmount";
    }

    return "";
  });

  const saveButtonDisabled = () => {
    if (
      campaignDiscountType === CampaignDiscountType.FixedPrice &&
      (offer.price === undefined || Number.isNaN(offer.price))
    ) {
      return true;
    }

    return false;
  };

  return (
    <>
      <MaterialTable
        title=" "
        columns={[
          {
            title: t("common.tableHeaders.ean"),
            field: "gtin",
            hidden: true,
            searchable: true
          },
          {
            title: t("common.tableHeaders.product"),
            field: "name",
            render: (row: Product) => {
              return (
                <div style={{ minWidth: 180 }}>
                  <Typography>{row.name}</Typography>
                  <Typography color="textSecondary">
                    {`${row.gtin}, `}
                    {row.details?.size ? `${row.details?.size}` : ""}{" "}
                    {row?.brandName}
                  </Typography>
                </div>
              );
            }
          },
          {
            title: t("common.tableHeaders.price"),
            field: "pricing.price",
            render: row => {
              return (
                <div>
                  <Typography>
                    <Currency
                      value={row.pricing.price}
                      priceUnit={row.pricing.priceUnit}
                    />
                  </Typography>
                </div>
              );
            }
          }
        ]}
        data={products.filter(p => !excludedProductIds?.includes(p.id))}
        isLoading={isLoading}
        actions={[
          {
            icon: "add_circle",
            iconProps: {
              style: { color: "rgb(31, 132, 52)" }
            },
            tooltip: t("common.actions.addToCampaign"),
            onClick: (event, rowData: Product | Product[]) => {
              setDialogOpenState(true);
              if (!Array.isArray(rowData)) {
                setOffer({
                  product: {
                    id: rowData.id,
                    name: rowData.name,
                    pricing: rowData.pricing
                  }
                });
              }
            }
          }
        ]}
        options={{
          actionsColumnIndex: -1,
          searchFieldAlignment: mobile ? "left" : "right",
          headerStyle: {
            padding: mobile ? "8px" : ""
          }
        }}
      />

      <Dialog
        fullWidth={true}
        maxWidth={"md"}
        open={dialogOpenState}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setDialogOpenState(false);
              setPurchaseRequirement("");
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: 20
            }}
          >
            <Typography variant="h4" gutterBottom component="div">
              {t("common.campaigns.campaignPriceDialogTitle")}
            </Typography>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: 30
            }}
          >
            <Typography variant="body1" gutterBottom>
              {t("common.campaigns.campaignPriceDialogDescription")}
            </Typography>
          </div>
          <div style={{ marginBottom: 10 }}>
            <>
              <Typography variant="body1" gutterBottom>
                {t("common.campaigns.originalPrice")}:{" "}
                <Currency
                  value={offer?.product?.pricing?.price || 0}
                  priceUnit={offer?.product?.pricing?.priceUnit}
                />
              </Typography>
              <TextField
                inputProps={{
                  maxLength: 35
                }}
                disabled={
                  campaignDiscountType !== CampaignDiscountType.FixedPrice
                }
                id="standard-basic"
                label={t("common.formFields.campaignPrice")}
                error={
                  campaignDiscountType === CampaignDiscountType.FixedPrice &&
                  (offer.price === undefined || Number.isNaN(offer.price))
                }
                type="number"
                style={{ width: 300 }}
                onChange={event => {
                  setOffer({
                    ...offer,
                    price: parseFloat(event.target.value)
                  });
                }}
              />
              {offer.price === 0 && (
                <Alert severity="warning" style={{ marginTop: 5 }}>
                  <AlertTitle>{t("common.messages.warning")}</AlertTitle>
                  {t("common.campaigns.productWillBeSoldForFree")} -{" "}
                  <strong>
                    <Currency value={0} />!
                  </strong>
                </Alert>
              )}
              {campaignDiscountType !== CampaignDiscountType.FixedPrice && (
                <Alert severity="warning" style={{ marginTop: 5 }}>
                  {t("common.messages.campaignDiscountTypeFixed")}{" "}
                  {t("common.messages.forTheProduct")}
                </Alert>
              )}
            </>
          </div>
          <div>
            <h3>{t("common.messages.selectCampaignPurchaseRequirement")}</h3>
            <FormControl>
              <RadioGroup
                row
                defaultValue={purchaseRequirement}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setPurchaseRequirement(event.target.value);
                  setOffer({
                    ...offer,
                    minimumPurchaseAmount: undefined,
                    minimumPurchaseQuantity: undefined
                  });
                }}
              >
                <FormControlLabel
                  value="ShoppingCartAmount"
                  control={<Radio />}
                  label={t("common.formFields.basedOnShoppingCartAmount")}
                />
                <FormControlLabel
                  value="NumberOfItems"
                  control={<Radio />}
                  label={t("common.formFields.basedOnNumberOfItems")}
                />
              </RadioGroup>
            </FormControl>
            {purchaseRequirement === "ShoppingCartAmount" && (
              <>
                <Alert severity="info" style={{ marginTop: 5 }}>
                  {t("common.campaigns.amountCustomerNeedsToSpend")}
                </Alert>
              </>
            )}
            {purchaseRequirement === "NumberOfItems" && (
              <Alert severity="info" style={{ marginTop: 5 }}>
                {t("common.campaigns.numberOfItemsCustomerNeedsToBuy")}
              </Alert>
            )}
          </div>
          <div>
            {purchaseRequirement === "ShoppingCartAmount" && (
              <TextField
                inputProps={{
                  maxLength: 35
                }}
                id="standard-basic"
                type="number"
                label={t("common.formFields.amount")}
                style={{ width: 300 }}
                onChange={event => {
                  setOffer({
                    ...offer,
                    minimumPurchaseAmount: parseInt(event.target.value)
                  });
                }}
              />
            )}
            {purchaseRequirement === "NumberOfItems" && (
              <TextField
                inputProps={{
                  maxLength: 35
                }}
                id="standard-basic"
                type="number"
                label={t("common.formFields.minQuantity")}
                style={{ width: 300 }}
                onChange={event => {
                  setOffer({
                    ...offer,
                    minimumPurchaseQuantity: parseInt(event.target.value)
                  });
                }}
              />
            )}
          </div>
          <div>
            <TextField
              inputProps={{
                maxLength: 35
              }}
              id="standard-basic"
              type="number"
              label={t("common.formFields.maxQuantity")}
              style={{ width: 300 }}
              onChange={event => {
                setOffer({
                  ...offer,
                  maximumPurchaseQuantity: parseInt(event.target.value)
                });
              }}
            />
          </div>
          <div
            style={{ display: "flex", justifyContent: "center", marginTop: 50 }}
          >
            <Button
              variant="contained"
              color="primary"
              style={{ width: 300 }}
              disabled={saveButtonDisabled()}
              onClick={() => {
                onSave(offer);
                setDialogOpenState(false);
              }}
            >
              {t("common.actions.addToCampaign")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
